"use client";

import { useState } from "react";
import type { MtbPreferences, PlannerObjectives, PlannerPreferences, SkiPreferences } from "@/lib/planner/preferences";
import { PLANNER_SPORT_FIELDS, type PrefField } from "@/lib/planner/planner-sport-config";
import { Field, Segmented, Toggle } from "@/app/_components/ui/controls";
import type { AreaSport } from "@/lib/planner/graph-types";
import {
  ALL_LIFT_GROUPS_ALLOWED,
  updateAllowedLiftTypes,
  type LiftTypeGroup,
} from "@/lib/planner/lift-preferences";

type BaseNodeOption = { id: string; label: string };

// This panel drives both resort sports. The ski arm renders the original
// toggle set verbatim (unchanged); the MTB arm renders its sport-specific axes
// from the config registry (lib/planner/planner-sport-config.ts). The shared
// day / start / finish / food / finish-toggle sections are rendered the same
// way for both sports so they stay consistent.
type Props = {
  prefs: PlannerPreferences;
  onChange: (
    next: PlannerPreferences,
    touchedPath?: string | readonly string[],
  ) => void;
  baseNodes: BaseNodeOption[];
  hasBuses: boolean;
  treeDataAvailable: boolean;
  /** Show the food/coffee/apres controls only when the area has place data. */
  placesAvailable: boolean;
  loading: boolean;
  /** Layerable fine-tune toggles flip a single objective weight on/off. */
  onToggleObjective: (objective: keyof PlannerPreferences["objectives"], on: boolean) => void;
  /** Open the detailed controls disclosure by default (the "Build your own" door). */
  fineTuneDefaultOpen?: boolean;
};

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[10px] border border-border-light bg-white px-3 text-[13.5px] text-text-body";

/** The MTB-specific axes the config drives directly (flat string keys). */
const MTB_AXIS_KEYS = [
  "ability",
  "difficultyMode",
  "ridingStyle",
  "fitness",
  "climbingTolerance",
  "distance",
  "bikeType",
  "group",
] as const;

function fieldByKey(fields: PrefField[], key: string): PrefField | undefined {
  return fields.find((f) => f.key === key);
}

function LiftChoiceRow<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-[12px] font-semibold text-text-body">{label}</p>
      <div className="flex gap-1.5" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={`min-h-[44px] flex-1 rounded-[10px] border px-2.5 text-[12px] font-semibold transition-colors ${
              value === option.value
                ? "border-navy bg-navy text-white"
                : "border-border-light bg-white text-text-body hover:border-gold/40"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PreferencesPanel(props: Props) {
  if (props.prefs.sport === "mtb") {
    return <MtbPanel {...props} prefs={props.prefs} />;
  }
  return <SkiPanel {...props} prefs={props.prefs} />;
}

// ---------------------------------------------------------------------------
// Shared header / day / time / start-point sections
// ---------------------------------------------------------------------------

function DayHeader({ loading }: { loading: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="font-serif text-[18px] font-bold text-navy">Your day</h2>
      {loading ? <span className="text-[11px] text-text-muted">Updating…</span> : null}
    </div>
  );
}

type FineTuneOption = {
  objective: keyof PlannerObjectives;
  label: string;
};

const SKI_FINE_TUNE_OPTIONS: FineTuneOption[] = [
  { objective: "sun", label: "Sun" },
  { objective: "scenic", label: "Views" },
  { objective: "firstTracks", label: "Early snow" },
  { objective: "coverage", label: "Cover ground" },
  { objective: "cruise", label: "Cruisy" },
  { objective: "quiet", label: "Quieter lifts" },
];

const MTB_FINE_TUNE_OPTIONS: FineTuneOption[] = [
  { objective: "sun", label: "Sun" },
  { objective: "scenic", label: "Views" },
  { objective: "firstTracks", label: "Bigger descents" },
  { objective: "coverage", label: "Cover ground" },
  { objective: "cruise", label: "Flow" },
  { objective: "quiet", label: "Quieter uplift" },
];

function FineTuneSection({
  sport,
  objectives,
  onToggleObjective,
}: {
  sport: AreaSport;
  objectives: PlannerPreferences["objectives"];
  onToggleObjective: (objective: keyof PlannerPreferences["objectives"], on: boolean) => void;
}) {
  const options = sport === "mtb" ? MTB_FINE_TUNE_OPTIONS : SKI_FINE_TUNE_OPTIONS;
  return (
    <div className="space-y-2">
      <h3 className="font-serif text-[16px] font-bold text-navy">Fine-tune layers</h3>
      {options.map((option) => (
        <Toggle
          key={option.objective}
          label={option.label}
          checked={(objectives[option.objective] ?? 0) > 0}
          onChange={(on) => onToggleObjective(option.objective, on)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ski panel (byte-identical to the original ski-only panel)
// ---------------------------------------------------------------------------

function SkiPanel({
  prefs,
  onChange,
  baseNodes,
  hasBuses,
  treeDataAvailable,
  placesAvailable,
  loading,
  onToggleObjective,
  fineTuneDefaultOpen,
}: Props & { prefs: SkiPreferences }) {
  const [liftError, setLiftError] = useState<string | null>(null);
  const set = <K extends keyof SkiPreferences>(
    key: K,
    value: SkiPreferences[K],
    touchedPath = String(key),
  ) => onChange({ ...prefs, [key]: value }, touchedPath);
  const allowedTypes = {
    ...ALL_LIFT_GROUPS_ALLOWED,
    ...(prefs.liftPrefs.allowedTypes ?? {}),
  };

  const setAllowedGroup = (
    group: LiftTypeGroup,
    allowed: boolean,
    nextLiftPrefs: SkiPreferences["liftPrefs"] = prefs.liftPrefs,
    touchedPaths: readonly string[] = [`liftPrefs.allowedTypes.${group}`],
  ) => {
    const update = updateAllowedLiftTypes(allowedTypes, group, allowed);
    if (update.rejected) {
      setLiftError("Keep at least one known lift type available.");
      return;
    }
    setLiftError(null);
    onChange(
      {
        ...prefs,
        liftPrefs: {
          ...nextLiftPrefs,
          allowedTypes: update.allowedTypes,
        },
      },
      touchedPaths,
    );
  };

  const dragMode = !allowedTypes.drags
    ? "never"
    : prefs.liftPrefs.avoidDrags
      ? "avoid"
      : "fine";

  return (
    <div className="space-y-5">
      <DayHeader loading={loading} />
      <FineTuneSection sport="ski" objectives={prefs.objectives} onToggleObjective={onToggleObjective} />
      <details open={fineTuneDefaultOpen} className="group space-y-5">
        <summary className="cursor-pointer list-none text-[13px] font-semibold text-gold">
          Fine-tune everything
        </summary>
        <div className="mt-4 space-y-5">

      <Field label="Day">
        <Segmented
          value={prefs.date}
          onChange={(v) => set("date", v)}
          options={[
            { value: "today", label: "Today" },
            { value: "tomorrow", label: "Tomorrow" },
          ]}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Start">
          <input
            type="time"
            value={prefs.startTime}
            onChange={(e) => set("startTime", e.target.value)}
            className={SELECT_CLASS}
          />
        </Field>
        <Field label="Finish">
          <input
            type="time"
            value={prefs.endTime}
            onChange={(e) => set("endTime", e.target.value)}
            className={SELECT_CLASS}
          />
        </Field>
      </div>

      {baseNodes.length > 0 ? (
        <Field label="Start point">
          <select
            value={prefs.startPoint}
            onChange={(e) => set("startPoint", e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="auto">Nearest to me</option>
            {baseNodes.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <Field label="Ability">
        <Segmented
          value={prefs.ability}
          onChange={(v) => set("ability", v)}
          options={[
            { value: "beginner", label: "Beginner" },
            { value: "intermediate", label: "Intermediate" },
            { value: "advanced", label: "Advanced" },
            { value: "expert", label: "Expert" },
          ]}
        />
      </Field>

      <Field label="Push yourself?">
        <Segmented
          value={prefs.difficultyMode}
          onChange={(v) => set("difficultyMode", v)}
          options={[
            { value: "comfort", label: "Comfort zone" },
            { value: "push", label: "Push a little" },
            { value: "challenge", label: "Challenge me" },
          ]}
        />
      </Field>

      <Field label="How far?">
        <Segmented
          value={prefs.distance}
          onChange={(v) => set("distance", v)}
          options={[
            { value: "short", label: "Short" },
            { value: "half-day", label: "Half day" },
            { value: "full-day", label: "Full day" },
            { value: "max", label: "Max km" },
          ]}
        />
      </Field>

      <Field label="Group">
        <Segmented
          value={prefs.group}
          onChange={(v) => set("group", v)}
          options={[
            { value: "solo", label: "Solo" },
            { value: "couple", label: "Couple" },
            { value: "family", label: "Family" },
            { value: "mates", label: "Mates" },
          ]}
        />
      </Field>

      <Field label="Flat sections">
        <Segmented
          value={prefs.poling}
          onChange={(v) => set("poling", v)}
          options={[
            { value: "fine", label: "Do not mind poling" },
            { value: "minimise", label: "Keep it rolling" },
          ]}
        />
      </Field>

      <Field label="Tree-lined runs">
        {treeDataAvailable ? (
          <Segmented
            value={prefs.treeRuns}
            onChange={(v) => set("treeRuns", v)}
            options={[
              { value: "prefer", label: "Prefer" },
              { value: "no-preference", label: "No preference" },
              { value: "avoid", label: "Avoid" },
            ]}
          />
        ) : (
          <p className="text-[12px] italic text-text-muted">No forest data for this area yet.</p>
        )}
      </Field>

      <div className="space-y-3 border-t border-border-light pt-4">
        <div>
          <h3 className="font-serif text-[16px] font-bold text-navy">Lifts</h3>
          <p className="mt-1 text-[12px] leading-snug text-text-muted">
            Shape the way up without changing the kind of terrain you asked for.
          </p>
        </div>

        <LiftChoiceRow
          label="Drag lifts"
          value={dragMode}
          options={[
            { value: "fine", label: "Fine" },
            { value: "avoid", label: "Avoid" },
            { value: "never", label: "Never" },
          ]}
          onChange={(mode) => {
            const allowed = mode !== "never";
            setAllowedGroup(
              "drags",
              allowed,
              { ...prefs.liftPrefs, avoidDrags: mode === "avoid" },
              ["liftPrefs.allowedTypes.drags", "liftPrefs.avoidDrags"],
            );
          }}
        />
        <LiftChoiceRow
          label="Gondolas and cabin lifts"
          value={allowedTypes.gondolas ? "on" : "off"}
          options={[
            { value: "on", label: "On" },
            { value: "off", label: "Off" },
          ]}
          onChange={(value) => setAllowedGroup("gondolas", value === "on")}
        />
        <LiftChoiceRow
          label="Chair lifts"
          value={allowedTypes.chairs ? "on" : "off"}
          options={[
            { value: "on", label: "On" },
            { value: "off", label: "Off" },
          ]}
          onChange={(value) => setAllowedGroup("chairs", value === "on")}
        />

        <button
          type="button"
          onClick={() => {
            setLiftError(null);
            onChange(
              {
                ...prefs,
                liftPrefs: {
                  ...prefs.liftPrefs,
                  avoidDrags: false,
                  preferGondolas: false,
                  allowedTypes: { drags: false, gondolas: false, chairs: true },
                },
              },
              [
                "liftPrefs.avoidDrags",
                "liftPrefs.preferGondolas",
                "liftPrefs.allowedTypes.drags",
                "liftPrefs.allowedTypes.gondolas",
                "liftPrefs.allowedTypes.chairs",
              ],
            );
          }}
          className="min-h-[44px] w-full rounded-[10px] border border-gold/40 bg-gold/[0.07] px-3 text-[13px] font-semibold text-navy"
        >
          Chair-led day
        </button>

        <Toggle
          label="Prefer gondolas"
          checked={prefs.liftPrefs.preferGondolas}
          onChange={(value) => set(
            "liftPrefs",
            { ...prefs.liftPrefs, preferGondolas: value },
            "liftPrefs.preferGondolas",
          )}
        />
        <Toggle
          label="Prefer faster lifts"
          checked={prefs.liftPrefs.preferFastLifts ?? false}
          onChange={(value) => set(
            "liftPrefs",
            { ...prefs.liftPrefs, preferFastLifts: value },
            "liftPrefs.preferFastLifts",
          )}
        />
        {liftError ? (
          <p role="alert" className="text-[12px] font-medium text-red-700">
            {liftError}
          </p>
        ) : null}
      </div>

      {placesAvailable ? (
        <div className="space-y-5 border-t border-border-light pt-4">
          <h3 className="font-serif text-[16px] font-bold text-navy">Food and drink</h3>

          <Field label="Lunch">
            <Segmented
              value={prefs.lunch.style}
              onChange={(v) => set("lunch", { ...prefs.lunch, style: v }, "lunch.style")}
              options={[
                { value: "skip", label: "Skip" },
                { value: "packed", label: "Backpack" },
                { value: "cheap", label: "Mountain café" },
                { value: "sit-down", label: "Sit-down" },
              ]}
            />
          </Field>

          {prefs.lunch.style !== "skip" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Lunch time">
                <input
                  type="time"
                  value={prefs.lunch.time}
                  onChange={(e) => set("lunch", { ...prefs.lunch, time: e.target.value }, "lunch.time")}
                  className={SELECT_CLASS}
                />
              </Field>
              {prefs.lunch.style !== "packed" ? (
                <Field label="Price">
                  <select
                    value={prefs.lunch.price}
                    onChange={(e) =>
                      set("lunch", { ...prefs.lunch, price: e.target.value as SkiPreferences["lunch"]["price"] }, "lunch.price")
                    }
                    className={SELECT_CLASS}
                  >
                    <option value="budget">Budget</option>
                    <option value="mid">Mid-range</option>
                    <option value="treat">Treat yourself</option>
                  </select>
                </Field>
              ) : null}
            </div>
          ) : null}

          <Field label="Coffee stops">
            <Segmented
              value={prefs.coffeeStops}
              onChange={(v) => set("coffeeStops", v)}
              options={[
                { value: "skip", label: "Skip" },
                { value: "morning", label: "Morning" },
                { value: "afternoon", label: "Afternoon" },
                { value: "both", label: "Both" },
              ]}
            />
          </Field>

          <Field label="Après">
            <Segmented
              value={prefs.apres}
              onChange={(v) => set("apres", v)}
              options={[
                { value: "skip", label: "Skip" },
                { value: "coffee-cake", label: "Coffee & cake" },
                { value: "quiet", label: "Quiet drink" },
                { value: "party", label: "Big party" },
              ]}
            />
          </Field>
        </div>
      ) : null}

      <div className="space-y-2 border-t border-border-light pt-4">
        <Toggle
          label="End where I started"
          checked={prefs.returnToStart}
          onChange={(v) => set("returnToStart", v)}
        />
        {hasBuses ? (
          <Toggle
            label="Happy to take the ski bus"
            checked={prefs.allowBus}
            onChange={(v) => set("allowBus", v)}
          />
        ) : null}
        <Toggle
          label="Include lifts with unknown status"
          checked={prefs.includeUnknownLifts}
          onChange={(v) => set("includeUnknownLifts", v)}
        />
      </div>
        </div>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MTB panel (config-driven sport axes + shared sections)
// ---------------------------------------------------------------------------

function MtbPanel({
  prefs,
  onChange,
  baseNodes,
  hasBuses,
  placesAvailable,
  loading,
  onToggleObjective,
  fineTuneDefaultOpen,
}: Props & { prefs: MtbPreferences }) {
  const set = <K extends keyof MtbPreferences>(
    key: K,
    value: MtbPreferences[K],
    touchedPath = String(key),
  ) => onChange({ ...prefs, [key]: value }, touchedPath);

  const fields = PLANNER_SPORT_FIELDS.mtb;

  // The riding axes are flat string keys (ability, ridingStyle, fitness,
  // climbingTolerance, distance, bikeType, group); drive them straight from
  // the config so the option vocabularies live in one place.
  const axisFields = MTB_AXIS_KEYS.map((key) => fieldByKey(fields, key)).filter(
    (f): f is PrefField => f != null,
  );

  const lunchField = fieldByKey(fields, "lunch");
  const coffeeField = fieldByKey(fields, "coffeeStops");
  const apresField = fieldByKey(fields, "apres");

  return (
    <div className="space-y-5">
      <DayHeader loading={loading} />
      <FineTuneSection sport="mtb" objectives={prefs.objectives} onToggleObjective={onToggleObjective} />
      <details open={fineTuneDefaultOpen} className="group space-y-5">
        <summary className="cursor-pointer list-none text-[13px] font-semibold text-gold">
          Fine-tune everything
        </summary>
        <div className="mt-4 space-y-5">

      <Field label="Day">
        <Segmented
          value={prefs.date}
          onChange={(v) => set("date", v)}
          options={[
            { value: "today", label: "Today" },
            { value: "tomorrow", label: "Tomorrow" },
          ]}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Start">
          <input
            type="time"
            value={prefs.startTime}
            onChange={(e) => set("startTime", e.target.value)}
            className={SELECT_CLASS}
          />
        </Field>
        <Field label="Finish">
          <input
            type="time"
            value={prefs.endTime}
            onChange={(e) => set("endTime", e.target.value)}
            className={SELECT_CLASS}
          />
        </Field>
      </div>

      {baseNodes.length > 0 ? (
        <Field label="Start point">
          <select
            value={prefs.startPoint}
            onChange={(e) => set("startPoint", e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="auto">Nearest to me</option>
            {baseNodes.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      {/* Lift pass vs pedal-only: a framing decision that changes the whole
          plan, so it sits above the riding axes. Pedal-only refuses every
          uplift and plans a human-powered loop. */}
      <Field label="Lifts">
        <Segmented
          value={prefs.liftAccess}
          onChange={(v) => set("liftAccess", v as MtbPreferences["liftAccess"])}
          options={[
            { value: "lift-pass", label: "Lift pass" },
            { value: "pedal-only", label: "Pedal only" },
          ]}
        />
        <p className="mt-1.5 text-[12px] leading-snug text-text-muted">
          {prefs.liftAccess === "pedal-only"
            ? "Earn every descent: no uplifts, a human-powered loop from your start."
            : "Uplifts where they help. Switch to pedal only for a no-pass, lift-free day."}
        </p>
      </Field>

      {/* Riding axes, config-driven. The config option values are exactly the
          MtbPreferences union members, so the cast through the field key is
          safe. In pedal-only mode the climbing-tolerance axis is moot (you
          climb everything), so it is hidden to avoid a contradictory control. */}
      {axisFields
        .filter((f) => !(prefs.liftAccess === "pedal-only" && f.key === "climbingTolerance"))
        .map((f) => (
        <Field key={f.key} label={f.label}>
          <Segmented
            value={prefs[f.key as keyof MtbPreferences] as string}
            options={f.options}
            onChange={(v) =>
              set(
                f.key as keyof MtbPreferences,
                v as MtbPreferences[keyof MtbPreferences],
              )
            }
          />
        </Field>
      ))}

      {placesAvailable ? (
        <div className="space-y-5 border-t border-border-light pt-4">
          <h3 className="font-serif text-[16px] font-bold text-navy">Food and drink</h3>

          {lunchField ? (
            <Field label={lunchField.label}>
              <Segmented
                value={prefs.lunch.style}
                options={lunchField.options}
                onChange={(v) =>
                  set("lunch", { ...prefs.lunch, style: v as MtbPreferences["lunch"]["style"] }, "lunch.style")
                }
              />
            </Field>
          ) : null}

          {prefs.lunch.style !== "skip" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Lunch time">
                <input
                  type="time"
                  value={prefs.lunch.time}
                  onChange={(e) => set("lunch", { ...prefs.lunch, time: e.target.value }, "lunch.time")}
                  className={SELECT_CLASS}
                />
              </Field>
              {prefs.lunch.style !== "packed" ? (
                <Field label="Price">
                  <select
                    value={prefs.lunch.price}
                    onChange={(e) =>
                      set("lunch", { ...prefs.lunch, price: e.target.value as MtbPreferences["lunch"]["price"] }, "lunch.price")
                    }
                    className={SELECT_CLASS}
                  >
                    <option value="budget">Budget</option>
                    <option value="mid">Mid-range</option>
                    <option value="treat">Treat yourself</option>
                  </select>
                </Field>
              ) : null}
            </div>
          ) : null}

          {coffeeField ? (
            <Field label={coffeeField.label}>
              <Segmented
                value={prefs.coffeeStops}
                options={coffeeField.options}
                onChange={(v) => set("coffeeStops", v as MtbPreferences["coffeeStops"])}
              />
            </Field>
          ) : null}

          {apresField ? (
            <Field label={apresField.label}>
              <Segmented
                value={prefs.apres}
                options={apresField.options}
                onChange={(v) => set("apres", v as MtbPreferences["apres"])}
              />
            </Field>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-2 border-t border-border-light pt-4">
        <Toggle
          label="End where I started"
          checked={prefs.returnToStart}
          onChange={(v) => set("returnToStart", v)}
        />
        {hasBuses ? (
          <Toggle
            label="Happy to take the shuttle bus"
            checked={prefs.allowBus}
            onChange={(v) => set("allowBus", v)}
          />
        ) : null}
        <Toggle
          label="Include lifts with unknown status"
          checked={prefs.includeUnknownLifts}
          onChange={(v) => set("includeUnknownLifts", v)}
        />
      </div>
        </div>
      </details>
    </div>
  );
}
